import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import PlaceIcon from '@mui/icons-material/Place';
import ChairIcon from '@mui/icons-material/Chair';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getAllArchivedSofas, getArchivedSofaDetails, unarchiveSofa, getCurrentUser } from '../api';

const Archive = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [archivedSofas, setArchivedSofas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [destinationFilter, setDestinationFilter] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedSofa, setSelectedSofa] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [unarchivingId, setUnarchivingId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  // Режим отображения: 'date' — общий список по дате архивации, 'folder' — группы по направлениям
  const [viewMode, setViewMode] = useState('date');
  const [sortBy, setSortBy] = useState('archived_at');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    getCurrentUser()
      .then((res) => setCurrentUserRole(res.data?.role))
      .catch(() => {});
  }, []);

  const fetchArchivedSofas = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (destinationFilter) {
        params.destination = destinationFilter;
      }
      const response = await getAllArchivedSofas(params);
      setArchivedSofas(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching archived sofas:', err);
      setError('Ошибка загрузки архива');
    } finally {
      setLoading(false);
    }
  }, [destinationFilter]);

  useEffect(() => {
    fetchArchivedSofas();
  }, [fetchArchivedSofas]);

  const groupedByDestination = archivedSofas.reduce((groups, sofa) => {
    const destination = sofa.destination || 'Без назначения';
    if (!groups[destination]) {
      groups[destination] = [];
    }
    groups[destination].push(sofa);
    return groups;
  }, {});

  const filterSofas = (sofas) => {
    if (!searchQuery) return sofas;
    const query = searchQuery.toLowerCase();
    return sofas.filter(sofa =>
      String(sofa.number ?? '').toLowerCase().includes(query) ||
      String(sofa.model_name ?? '').toLowerCase().includes(query) ||
      String(sofa.fabric?.name ?? '').toLowerCase().includes(query) ||
      String(sofa.destination ?? '').toLowerCase().includes(query) ||
      String(sofa.archived_reason ?? '').toLowerCase().includes(query)
    );
  };

  const uniqueDestinations = [...new Set(archivedSofas.map(s => s.destination).filter(Boolean))];

  // Плоский список с поиском и сортировкой (для режима "Список по дате")
  const flatSofas = (() => {
    const list = filterSofas(archivedSofas);
    const sorted = [...list].sort((a, b) => {
      let av, bv;
      if (sortBy === 'archived_at' || sortBy === 'delivery_date') {
        av = a[sortBy] ? new Date(a[sortBy]).getTime() : 0;
        bv = b[sortBy] ? new Date(b[sortBy]).getTime() : 0;
      } else if (sortBy === 'number') {
        // Номер дивана — пытаемся как число, иначе как строку
        const an = parseInt(a.number, 10); const bn = parseInt(b.number, 10);
        av = isNaN(an) ? (a.number || '') : an;
        bv = isNaN(bn) ? (b.number || '') : bn;
      } else {
        av = (a[sortBy] || '').toString().toLowerCase();
        bv = (b[sortBy] || '').toString().toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  })();

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir(column === 'archived_at' || column === 'delivery_date' ? 'desc' : 'asc');
    }
  };

  const handleAccordionChange = (destination) => (event, isExpanded) => {
    setExpandedGroups(prev => ({
      ...prev,
      [destination]: isExpanded
    }));
  };

  const handleViewDetails = (sofa) => {
    setSelectedSofa(sofa);
    setDetailsOpen(true);
    setDetails(null);
    setDetailsLoading(true);
    getArchivedSofaDetails(sofa.id)
      .then((res) => setDetails(res.data))
      .catch((err) => console.error('Error fetching archive details:', err))
      .finally(() => setDetailsLoading(false));
  };

  const handleUnarchive = async (sofa) => {
    if (!window.confirm(`Вернуть диван №${sofa.number} в план производства?`)) return;
    try {
      setUnarchivingId(sofa.id);
      await unarchiveSofa(sofa.id);
      setDetailsOpen(false);
      await fetchArchivedSofas();
    } catch (err) {
      console.error('Error unarchiving sofa:', err);
      alert(err.response?.data?.detail || 'Ошибка при возврате из архива');
    } finally {
      setUnarchivingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      delivered: 'success',
      shipped: 'info',
      defect: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const renderMobileCard = (sofa) => (
    <Card
      key={sofa.id}
      variant="outlined"
      sx={{
        mb: 1,
        borderRadius: 2,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:active': { transform: 'scale(0.995)' },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box display="flex" alignItems="center" gap={0.75} mb={0.25}>
              <ChairIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                className="tnum"
                sx={{ letterSpacing: '-0.01em' }}
                noWrap
              >
                {sofa.number || '—'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.primary" noWrap>
              {sofa.model_name || sofa.model?.name || '—'}
            </Typography>
          </Box>
          <Chip
            label={sofa.status}
            size="small"
            color={getStatusColor(sofa.status)}
            sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', height: 22 }}
          />
        </Box>

        <Stack spacing={0.5} sx={{ pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary">Ткань</Typography>
            <Typography variant="caption" sx={{ maxWidth: '60%', textAlign: 'right' }} noWrap>
              {sofa.fabric?.name || '—'}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">Архивирован</Typography>
            <Typography variant="caption" className="tnum">
              {formatDate(sofa.archived_at)}
            </Typography>
          </Box>
          {sofa.archived_reason && (
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Причина</Typography>
              <Typography variant="caption" sx={{ maxWidth: '60%', textAlign: 'right' }} noWrap>
                {sofa.archived_reason}
              </Typography>
            </Box>
          )}
        </Stack>

        <Box display="flex" justifyContent="flex-end" gap={0.5} mt={1}>
          <Tooltip title="Подробнее">
            <IconButton size="small" onClick={() => handleViewDetails(sofa)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {currentUserRole === 'admin' && (
            <Tooltip title="Вернуть в план">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={unarchivingId === sofa.id}
                  onClick={() => handleUnarchive(sofa)}
                >
                  <UnarchiveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Заголовок */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: { xs: 2, md: 3 },
      }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 38, md: 40 }, height: { xs: 38, md: 40 } }}>
          <ArchiveIcon />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            }}
          >
            Архив
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Готовые и отправленные диваны
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', flexShrink: 0 }}>
          <Chip
            label={`Всего: ${archivedSofas.length}`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Фильтры */}
      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Поиск по номеру, модели, ткани..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: 'auto', sm: 300 }, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: 'auto', sm: 200 } }}>
            <InputLabel>Место назначения</InputLabel>
            <Select
              value={destinationFilter}
              onChange={(e) => setDestinationFilter(e.target.value)}
              label="Место назначения"
            >
              <MenuItem value="">Все</MenuItem>
              {uniqueDestinations.map(dest => (
                <MenuItem key={dest} value={dest}>{dest}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(e, v) => { if (v) setViewMode(v); }}
            sx={{ ml: { sm: 'auto' } }}
          >
            <ToggleButton value="date">Список (по дате)</ToggleButton>
            <ToggleButton value="folder">По папкам</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* Статистика по группам — только в режиме папок, на мобиле скрываем */}
      {viewMode === 'folder' && !isXs && (
        <Box mb={3}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {Object.entries(groupedByDestination).map(([destination, sofas]) => (
              <Chip
                key={destination}
                icon={<PlaceIcon />}
                label={`${destination}: ${sofas.length}`}
                variant="outlined"
                onClick={() => {
                  setExpandedGroups(prev => ({
                    ...prev,
                    [destination]: !prev[destination]
                  }));
                }}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* ── Режим "Список по дате": общий список, сортируемые колонки ── */}
      {viewMode === 'date' && (
        flatSofas.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ArchiveIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchQuery || destinationFilter ? 'Ничего не найдено' : 'Архив пуст'}
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Мобильные карточки */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {flatSofas.map(renderMobileCard)}
            </Box>
            {/* Десктопная таблица с сортировкой */}
            <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sortDirection={sortBy === 'archived_at' ? sortDir : false}>
                      <TableSortLabel active={sortBy === 'archived_at'} direction={sortDir} onClick={() => handleSort('archived_at')}>
                        Дата архивации
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'number' ? sortDir : false}>
                      <TableSortLabel active={sortBy === 'number'} direction={sortDir} onClick={() => handleSort('number')}>
                        № Заказа
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortBy === 'model_name' ? sortDir : false}>
                      <TableSortLabel active={sortBy === 'model_name'} direction={sortDir} onClick={() => handleSort('model_name')}>
                        Модель
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Ткань</TableCell>
                    <TableCell sortDirection={sortBy === 'destination' ? sortDir : false}>
                      <TableSortLabel active={sortBy === 'destination'} direction={sortDir} onClick={() => handleSort('destination')}>
                        Направление
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Причина</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flatSofas.map((sofa) => (
                    <TableRow key={sofa.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(sofa.archived_at)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <ChairIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="medium">{sofa.number || '—'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2">{sofa.model_name || sofa.model?.name || '-'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{sofa.fabric?.name || '-'}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{sofa.destination || '-'}</Typography></TableCell>
                      <TableCell><Chip label={sofa.status} size="small" color={getStatusColor(sofa.status)} /></TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{sofa.archived_reason || '-'}</Typography></TableCell>
                      <TableCell align="center">
                        <Tooltip title="Подробнее">
                          <IconButton size="small" onClick={() => handleViewDetails(sofa)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {currentUserRole === 'admin' && (
                          <Tooltip title="Вернуть в план">
                            <span>
                              <IconButton size="small" color="primary" disabled={unarchivingId === sofa.id} onClick={() => handleUnarchive(sofa)}>
                                <UnarchiveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )
      )}

      {/* ── Режим "По папкам": группы-аккордеоны по направлениям ── */}
      {viewMode === 'folder' && (
        Object.entries(groupedByDestination).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ArchiveIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Архив пуст
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Диваны будут добавляться сюда после отправки
          </Typography>
        </Paper>
      ) : (
        Object.entries(groupedByDestination)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([destination, sofas]) => {
            const filteredSofas = filterSofas(sofas);
            if (filteredSofas.length === 0 && searchQuery) return null;

            return (
              <Accordion
                key={destination}
                expanded={expandedGroups[destination] || false}
                onChange={handleAccordionChange(destination)}
                sx={{ mb: 1 }}
                disableGutters={isXs}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: { xs: 52, md: 56 } }}>
                  <Box display="flex" alignItems="center" width="100%">
                    <Avatar sx={{
                      bgcolor: 'secondary.main',
                      mr: { xs: 1.25, md: 2 },
                      width: { xs: 32, md: 36 },
                      height: { xs: 32, md: 36 },
                    }}>
                      <PlaceIcon fontSize="small" />
                    </Avatar>
                    <Box flex={1} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ fontSize: { xs: '0.95rem', md: '1rem' } }}
                        noWrap
                      >
                        {destination}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {filteredSofas.length} {filteredSofas.length === 1 ? 'диван' :
                          filteredSofas.length < 5 ? 'дивана' : 'диванов'}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1, md: 2 } }}>
                  {/* Мобильные карточки */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {filteredSofas.map(renderMobileCard)}
                  </Box>

                  {/* Десктопная таблица */}
                  <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>№ Заказа</strong></TableCell>
                          <TableCell><strong>Модель</strong></TableCell>
                          <TableCell><strong>Ткань</strong></TableCell>
                          <TableCell><strong>Статус</strong></TableCell>
                          <TableCell><strong>Дата архивации</strong></TableCell>
                          <TableCell><strong>Причина</strong></TableCell>
                          <TableCell align="center"><strong>Действия</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSofas.map((sofa) => (
                          <TableRow key={sofa.id} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <ChairIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" fontWeight="medium">
                                  {sofa.number || '—'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {sofa.model_name || sofa.model?.name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {sofa.fabric?.name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={sofa.status}
                                size="small"
                                color={getStatusColor(sofa.status)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="caption">
                                  {formatDate(sofa.archived_at)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {sofa.archived_reason || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Подробнее">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(sofa)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {currentUserRole === 'admin' && (
                                <Tooltip title="Вернуть в план">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={unarchivingId === sofa.id}
                                      onClick={() => handleUnarchive(sofa)}
                                    >
                                      <UnarchiveIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })
      ))}

      {/* Диалог с подробностями */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <ChairIcon sx={{ mr: 1 }} />
            Диван №{selectedSofa?.number}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSofa && (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Основная информация
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Модель:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedSofa.model_name || selectedSofa.model?.name || '-'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Ткань:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedSofa.fabric?.name || '-'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Место назначения:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedSofa.destination || '-'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Статус:</Typography>
                      <Chip label={selectedSofa.status} size="small" color={getStatusColor(selectedSofa.status)} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <LocalShippingIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                    Информация об архивации
                  </Typography>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Дата архивации:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatDate(selectedSofa.archived_at)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Причина:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedSofa.archived_reason || '-'}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Партия доставки:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedSofa.delivery_batch_id || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {selectedSofa.notes && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Примечания
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {selectedSofa.notes}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    👷 Работники, делавшие этот диван
                  </Typography>
                  {detailsLoading ? (
                    <CircularProgress size={20} />
                  ) : details?.workers ? (
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Каркас:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {details.workers.frame_worker?.full_name || '—'}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Швея:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {details.workers.sewer?.full_name || '—'}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Обивщик:</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {details.workers.upholsterer?.full_name || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Работники не были назначены</Typography>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    📜 История действий
                  </Typography>
                  {detailsLoading ? (
                    <CircularProgress size={20} />
                  ) : details?.history?.length > 0 ? (
                    <Stack spacing={1.5}>
                      {details.history.map((h, i) => (
                        <Box key={i} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, py: 0.5 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="baseline">
                            <Chip label={h.action} size="small" sx={{ mb: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(h.created_at)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 0.25 }}>
                            {h.description || '—'}
                          </Typography>
                          {h.user_name && (
                            <Typography variant="caption" color="text.secondary">
                              👤 {h.user_name}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">История пуста</Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {currentUserRole === 'admin' && selectedSofa && (
            <Button
              onClick={() => handleUnarchive(selectedSofa)}
              color="primary"
              variant="contained"
              startIcon={<UnarchiveIcon />}
              disabled={unarchivingId === selectedSofa.id}
            >
              Вернуть в план
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Archive;
